import { ChangeEvent, FC, KeyboardEvent, useEffect, useRef, useState } from "react";
import cx from "classnames";
import { useDispatch } from "react-redux";
import useShallowSelector from "../../hooks/useShalowSelector";
import { selectChatLoading, selectChatMessages, selectChatOpen } from "../../store/chat/selectors";
import { addMessage, sendMessageRequest, toggleChatBot } from "../../store/chat/actionCreators";
import { selectAuthUser } from "../../store/auth/selectors";

import styles from "./styles.module.scss";

const ChatBot: FC = () => {
    const dispatch = useDispatch();
    const scrollRef = useRef<HTMLDivElement>(null);
    const user = useShallowSelector(selectAuthUser);
    const isOpen = useShallowSelector(selectChatOpen);
    const messages = useShallowSelector(selectChatMessages);
    const loading = useShallowSelector(selectChatLoading);

    const [input, setInput] = useState<string>("");

    useEffect(() => {
        if (!messages || !scrollRef.current) return;
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages, scrollRef, loading]);

    if (!user?._id) return null;

    const handleInputChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        setInput(e.target.value);
    };

    const handleSendMessage = () => {
        if (!input.trim() || !user?._id) return;

        dispatch(addMessage({ message: input }));
        dispatch(sendMessageRequest({ message: input }));
        setInput("");
    };

    const handleTextAreaKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === "Enter" && event.shiftKey) {
            setInput(prevInput => prevInput + "\n");
            event.preventDefault();
        } else if (event.key === "Enter") {
            event.preventDefault();
            handleSendMessage();
        }
    };

    return (
        <div className={`${styles.chatContainer} ${isOpen ? styles.open : ""}`}>
            <div
                className={cx(styles.chatHeader, {
                    [styles.closed]: !isOpen,
                })}
                onClick={() => dispatch(toggleChatBot({ isOpen: !isOpen }))}
            >
                Boom Bot
            </div>
            {isOpen && (
                <div className={styles.chatBody}>
                    <div className={styles.messages} ref={scrollRef}>
                        {messages.map(msg => (
                            <div
                                key={msg.id}
                                className={`${styles.message} ${msg.role === "user" ? styles.user : styles.assistant}`}
                            >
                                {msg.content}
                            </div>
                        ))}
                        {loading && <div className={styles.loading}>Typing...</div>}
                    </div>
                    <div className={styles.chatInputContainer}>
                        <textarea
                            value={input}
                            onChange={handleInputChange}
                            placeholder="Write your question ..."
                            className={styles.chatInput}
                            autoFocus
                            onKeyDown={handleTextAreaKeyDown}
                        />
                        <button onClick={handleSendMessage} className={styles.sendButton} disabled={loading}>
                            Send
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ChatBot;
